// src/components/feature/ProductCard.tsx
//
// A compact card showing a single product in the catalog grid.
// Used by Catalog and search results.

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import { useCatalogProductName } from "@/lib/catalogI18n";
import { FavoriteButton } from "@/components/feature/FavoriteButton";

/**
 * Default display: just the hero image.
 * Card-level concern — used as a fallback if no image given.
 */
export interface ProductCardProps {
  product: {
    sku: string;
    name: string;
    /** Canonical vendor display name (Shop-owned enrichment) — preferred
     * headline over FileMaker's raw name when present. */
    displayName?: string | null;
    brand: string;
    category: string;
    priceEUR: number;
    currency: "EUR" | "USD" | "GBP";
    image: string;
    images?: string[];
    inStock: boolean | null;
    stockStatus?: "UNKNOWN" | undefined;
    featured?: boolean;
    approved?: "approved" | "pending" | "rejected";
    /** Real taxonomy label (migration 024); wins over the legacy chip. */
    taxonomyLabel?: { slug: string; en: string; fr: string; nl: string } | null;
  };
  /** Optional admin/staff-only actions rendered above the image
   *  (e.g. Hide / Show). When omitted, no actions strip is shown. */
  actions?: React.ReactNode;
}

export function ProductCard({ product, actions }: ProductCardProps) {
  const { t, i18n } = useTranslation();
  const availabilityUnknown = product.stockStatus === "UNKNOWN" || product.inStock == null;
  const isApproved = product.approved === "approved" || product.approved === undefined;
  // Catalog-only translation: French / Dutch locale users see the
  // translated product name when a reviewed DB translation exists.
  // English and missing translations fall back synchronously to the
  // vendor display name (canonical catalogue naming) or, failing that,
  // FileMaker's raw name — no blocking, no flash.
  const displayName = useCatalogProductName(
    product.sku, product.displayName ?? product.name);
  // The catalogue sku IS the product_id (mapApiProduct sets it). A
  // non-numeric sku (demo data) has no catalogue row to favourite.
  const productId = Number(product.sku);
  const favoriteId = Number.isInteger(productId) ? productId : null;
  return (
    // The card is one link. Interactive controls (favourites, admin
    // hide/show) therefore live OUTSIDE the <Link> as siblings — a
    // <button> inside an <a> is invalid HTML and breaks both the
    // keyboard and the click behaviour.
    <div className="relative group h-full">
      <Link
        to={`/catalog/${encodeURIComponent(product.sku)}`}
        className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas rounded-md"
      >
        <Card className="h-full overflow-hidden transition-colors group-hover:border-benchmark-teal/40">
          {/* Image — try the real product image; fall back to icon if it fails */}
          <div
            className={cn(
              "relative aspect-[4/3] w-full overflow-hidden bg-bg-elevated",
              "flex items-center justify-center",
            )}
            aria-hidden
          >
            {product.image && product.image !== "x" ? (
              <img
                src={product.image}
                alt={product.name}
                loading="lazy"
                className="h-full w-full object-cover"
                style={{ objectPosition: "center center" }}
              />
            ) : (
              <ShoppingBag className="h-10 w-10 text-fg-quaternary" strokeWidth={1} />
            )}
            {/* Availability chip (top-left); UNKNOWN is not an OUT promise. */}
            <div className="absolute left-2 top-2 flex flex-col gap-1">
              <Badge variant={!availabilityUnknown && product.inStock ? "success" : "default"} size="sm">
                {availabilityUnknown
                  ? t("product.availabilityUnknown", "Availability not verified")
                  : product.inStock
                    ? t("product.inStock", "In stock")
                    : t("product.outOfStock", "Out of stock")}
              </Badge>
              {!isApproved && (
                <Badge variant="warning" size="sm">
                  Pending review
                </Badge>
              )}
            </div>
            {/* Featured chip (top-right) */}
            {product.featured && (
              <div className="absolute right-2 top-2">
                <Badge variant="teal" size="sm">
                  Featured
                </Badge>
              </div>
            )}
          </div>
          {/* Body */}
          <div className="space-y-2 p-4">
            {/* Brand + category row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="rounded bg-bg-elevated px-1.5 py-0.5 text-[10px] font-510 text-fg-secondary">
                  {product.brand}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-fg-quaternary">
                  {product.taxonomyLabel
                    ? (product.taxonomyLabel[
                        (i18n.language?.slice(0, 2) ?? "en") as "en" | "fr" | "nl"
                      ] ?? product.taxonomyLabel.en)
                    : product.category}
                </span>
              </div>
              {product.images && product.images.length > 0 && (
                <span className="flex items-center gap-1 text-[10px] text-fg-quaternary">
                  <span className="h-1 w-1 rounded-full bg-fg-quaternary" />
                  <span className="h-1 w-1 rounded-full bg-fg-quaternary" />
                  <span className="h-1 w-1 rounded-full bg-fg-quaternary" />
                </span>
              )}
            </div>
            {/* Name */}
            <h3 className="line-clamp-2 text-sm font-510 tracking-tight text-fg-primary">
              {displayName}
            </h3>
            {/* Price + CTA */}
            <div className="flex items-center justify-between pt-1">
              <span className="font-mono text-sm font-510 text-fg-primary">
                {product.priceEUR > 0
                  ? product.priceEUR.toLocaleString("en-US", { style: "currency", currency: product.currency, minimumFractionDigits: 2 })
                  : "Price on request"}
              </span>
              <span className="flex items-center gap-1 text-xs text-benchmark-teal-bright">
                View
                <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
              </span>
            </div>
          </div>
        </Card>
      </Link>
      {/* Control overlay: the same box as the card image, so the
          controls land on the image. It never intercepts clicks
          outside its own controls, so the rest of the card still
          navigates. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 aspect-[4/3]">
        {/* Admin actions (Hide/Show), top-right — same place as before. */}
        {actions && (
          <div className="pointer-events-auto absolute right-2 top-2 z-10">
            {actions}
          </div>
        )}
        {favoriteId !== null && (
          <div className="pointer-events-auto absolute bottom-2 right-2 z-10">
            <FavoriteButton productId={favoriteId} productName={displayName} />
          </div>
        )}
      </div>
    </div>
  );
}
